import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { Form } from "react-bootstrap";
import Select from "react-select";
import { TbFilterCog } from "react-icons/tb";
import Cookies from "js-cookie";
import { supabase } from "../../supabaseClient";
import PortalDropdown from "../common/PortalDropdown";
import QuickIdeaPopup from "../common/QuickIdeaPopup";
import IdeasExpandMorePopup from "../common/IdeasExpandMorePopup";
import IdeasExpandAllPopup from "../common/IdeasExpandAllPopup";
import ConfirmationPopup from "../common/ConfirmationPopup";

const MAX_VISIBLE_IDEAS = 5;

function formatIdeaDate(value) {
  if (!value) return "Recently saved";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Recently saved";

  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function getIdeaTags(idea) {
  return Array.isArray(idea?.tags) ? idea.tags.filter(Boolean) : [];
}

function getIdeaAttachments(idea) {
  return Array.isArray(idea?.attachment_urls)
    ? idea.attachment_urls.filter(Boolean)
    : [];
}

function stripHtml(value) {
  if (!value) return "";

  if (typeof window !== "undefined") {
    const parsed = new window.DOMParser().parseFromString(value, "text/html");
    return parsed.body.textContent || "";
  }

  return value.replace(/<[^>]*>/g, " ");
}

const IdeasList = forwardRef(({ userId }, ref) => {
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [showAddIdeaModal, setShowAddIdeaModal] = useState(false);
  const [ideaToEdit, setIdeaToEdit] = useState(null);
  const [ideaToDelete, setIdeaToDelete] = useState(null);
  const [isDeletingIdea, setIsDeletingIdea] = useState(false);
  const [showBulkDeleteConfirmation, setShowBulkDeleteConfirmation] = useState(false);
  const [isBulkDeletingIdeas, setIsBulkDeletingIdeas] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showIdeasModal, setShowIdeasModal] = useState(false);
  const [showIdeasChatModal, setShowIdeasChatModal] = useState(false);

  const fetchIdeas = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      setError("");
      setIdeas([]);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const { data, error: fetchError } = await supabase
        .from("quick_ideas")
        .select("id, title, description, attachment_urls, tags, created_at")
        .eq("user_id", userId)
        .order("created_at", { ascending: false });

      if (fetchError) {
        if (fetchError.message?.includes("does not exist")) {
          setError("Run `migrations/040_quick_ideas.sql` to load saved ideas.");
          setIdeas([]);
          return;
        }

        throw fetchError;
      }

      setIdeas(data || []);
    } catch (loadError) {
      setError(loadError?.message || "Ideas could not be loaded.");
      setIdeas([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useImperativeHandle(
    ref,
    () => ({
      triggerShowAll: () => {
        setShowIdeasChatModal(true);
        fetchIdeas();
      },
    }),
    [fetchIdeas],
  );

  useEffect(() => {
    fetchIdeas();
  }, [fetchIdeas]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleIdeasChanged = () => {
      fetchIdeas();
    };

    window.addEventListener("quick-ideas:changed", handleIdeasChanged);

    return () => {
      window.removeEventListener("quick-ideas:changed", handleIdeasChanged);
    };
  }, [fetchIdeas]);

  const availableTags = Array.from(
    new Set(
      ideas
        .flatMap((idea) => getIdeaTags(idea))
        .map((tag) => tag.trim())
        .filter(Boolean),
    ),
  ).sort((left, right) => left.localeCompare(right));

  const availableTagOptions = availableTags.map((tag) => ({
    value: tag,
    label: tag,
  }));

  const selectedTagValues = selectedTags.map((tag) => tag.value.toLowerCase());

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();

  const filteredIdeas = ideas.filter((idea) => {
    const tags = getIdeaTags(idea);
    const attachments = getIdeaAttachments(idea);
    const searchableText = [
      idea.title || "",
      stripHtml(idea.description || ""),
      tags.join(" "),
      attachments.join(" "),
    ]
      .join(" ")
      .toLowerCase();

    const matchesSearch =
      !normalizedSearchTerm || searchableText.includes(normalizedSearchTerm);
    const matchesTag =
      selectedTagValues.length === 0 ||
      tags.some((tag) => selectedTagValues.includes(tag.toLowerCase()));

    return matchesSearch && matchesTag;
  });

  const currentSessionId = Cookies.get("chat_session_id");
  const normalizedSessionId =
    currentSessionId && currentSessionId !== "undefined" ? currentSessionId : null;
  const hasActiveFilters = Boolean(searchTerm || selectedTags.length);
  const visibleIdeas = filteredIdeas.slice(0, MAX_VISIBLE_IDEAS);

  const handleOpenCreate = () => {
    setIdeaToEdit(null);
    setShowAddIdeaModal(true);
  };

  const handleOpenEdit = (idea) => {
    setIdeaToEdit(idea);
    setShowAddIdeaModal(true);
  };

  const handleCloseModal = () => {
    setShowAddIdeaModal(false);
    setIdeaToEdit(null);
  };

  const handleDeleteIdea = async (idea) => {
    if (!idea?.id || !userId) return false;

    setIsDeletingIdea(true);
    setError("");

    try {
      const { error: deleteError } = await supabase
        .from("quick_ideas")
        .delete()
        .eq("id", idea.id)
        .eq("user_id", userId);

      if (deleteError) throw deleteError;

      setIdeas((currentIdeas) => currentIdeas.filter((currentIdea) => currentIdea.id !== idea.id));
      setIdeaToDelete(null);

      if (ideaToEdit?.id === idea.id) {
        handleCloseModal();
      }

      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("quick-ideas:changed", {
            detail: {
              action: "deleted",
              idea: {
                id: idea.id,
              },
            },
          }),
        );
      }

      return true;
    } catch (deleteIdeaError) {
      setError(deleteIdeaError?.message || "The ideas could not be deleted.");
      return false;
    } finally {
      setIsDeletingIdea(false);
    }
  };

  const requestDeleteIdea = (idea) => {
    setIdeaToDelete(idea);
  };

  const handleBulkDeleteIdeas = async () => {
    if (!userId || filteredIdeas.length === 0) return false;

    const ideaIds = filteredIdeas.map((idea) => idea.id).filter(Boolean);
    if (ideaIds.length === 0) return false;

    setIsBulkDeletingIdeas(true);
    setError("");

    try {
      const { error: deleteError } = await supabase
        .from("quick_ideas")
        .delete()
        .eq("user_id", userId)
        .in("id", ideaIds);

      if (deleteError) throw deleteError;

      setIdeas((currentIdeas) => currentIdeas.filter((idea) => !ideaIds.includes(idea.id)));
      setShowBulkDeleteConfirmation(false);

      if (ideaToEdit?.id && ideaIds.includes(ideaToEdit.id)) {
        handleCloseModal();
      }

      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("quick-ideas:changed", {
            detail: {
              action: "bulk-deleted",
              ideaIds,
            },
          }),
        );
      }

      return true;
    } catch (bulkDeleteError) {
      setError(bulkDeleteError?.message || "Ideas could not be deleted.");
      return false;
    } finally {
      setIsBulkDeletingIdeas(false);
    }
  };

  return (
    <>
      <div>
        <div
          className="checklist-tabs-header ideas-sidebar__section-header"
          style={{ padding: "0 1.5em", paddingLeft: "36px", marginBottom: "10px" }}
        >
          <span className="ideas-sidebar__section-title">Ideas</span>

          <div className="ideas-sidebar__section-actions">
            <button
              type="button"
              className="ideas-sidebar__header-add"
              onClick={handleOpenCreate}
              title="Add ideas"
            >
              <i className="bi bi-plus"></i>
            </button>
            <button
              type="button"
              className={`filter-toggle-btn ${showFilters || hasActiveFilters ? "active" : ""}`}
              onClick={() => setShowFilters((current) => !current)}
              title={showFilters || hasActiveFilters ? "Hide filters" : "Show filters"}
            >
              <TbFilterCog size={16} />
            </button>
          </div>
        </div>

        {(showFilters || hasActiveFilters) ? (
          <>
            <div className="d-flex mb-3 gap-2 ideas-sidebar__filter-row">
              <Select
                isMulti
                isSearchable
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                placeholder="Select tags"
                className="custom-select-container flex-grow-1"
                classNamePrefix="custom-select"
                options={availableTagOptions}
                value={selectedTags}
                onChange={(options) => setSelectedTags(Array.isArray(options) ? options : [])}
              />

              {hasActiveFilters ? (
                <button
                  type="button"
                  className="ideas-sidebar__clear-btn"
                  onClick={() => {
                    setSearchTerm("");
                    setSelectedTags([]);
                  }}
                  title="Clear filters"
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              ) : null}
            </div>

            <div className="d-flex mb-3 gap-2 ideas-sidebar__filter-row">
              <div className="search-input-wrapper flex-grow-1">
                <Form.Control
                  type="text"
                  className="search-input-box"
                  placeholder="Search ideas..."
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                />
                {searchTerm ? (
                  <i
                    className="bi bi-x search-clear-icon"
                    onClick={() => setSearchTerm("")}
                    aria-label="Clear ideas search"
                    title="Clear search"
                  ></i>
                ) : null}
              </div>
            </div>
          </>
        ) : null}

        <hr />

        <div className="d-flex flex-column">
          <div className="task-list ideas-sidebar__list" style={{ padding: "0em 1.5em" }}>
            {error ? <div className="text-center ideas-sidebar__feedback">{error}</div> : null}

            {loading ? (
              <div className="text-center ideas-sidebar__feedback">Loading ideas...</div>
            ) : null}

            {!loading && !error && filteredIdeas.length === 0 ? (
              <div className="text-center ideas-sidebar__feedback">
                {ideas.length === 0
                  ? "No ideas saved yet."
                  : "No ideas match the current filters."}
              </div>
            ) : null}

            {!loading &&
              !error &&
              visibleIdeas.map((idea) => {
                const tags = getIdeaTags(idea);
                const displayTags = tags.length > 0 ? tags.slice(0, 2) : ["No tags"];
                const remainingTagCount = tags.length > 2 ? tags.length - 2 : 0;
                return (
                  <article
                    key={idea.id}
                    className="task-card ideas-sidebar__card"
                  >
                    <div className="ideas-sidebar__card-shell">
                      <div
                        className="ideas-sidebar__card-main"
                        onClick={() => handleOpenEdit(idea)}
                      >
                        <div className="task-card-header ideas-sidebar__card-header">
                          <div className="ideas-sidebar__lead-icon">
                            <i className="bi bi-lightbulb"></i>
                          </div>

                          <div className="task-card-content ideas-sidebar__content">
                            <h4 className="task-title ideas-sidebar__card-title">
                              {idea.title || "Untitled ideas"}
                            </h4>
                          </div>
                        </div>

                        <div className="ideas-sidebar__inline-tags">
                          {displayTags.map((tag) => (
                            <span
                              className={`ideas-sidebar__inline-tag ${tags.length === 0 ? "ideas-sidebar__inline-tag--empty" : ""}`}
                              key={`${idea.id}-${tag}`}
                            >
                              {tag}
                            </span>
                          ))}

                          {remainingTagCount > 0 ? (
                            <span className="ideas-sidebar__inline-tag ideas-sidebar__inline-tag--count">
                              +{remainingTagCount}
                            </span>
                          ) : null}
                        </div>

                        <div className="ideas-sidebar__card-meta">
                          <span>{formatIdeaDate(idea.created_at)}</span>
                        </div>
                      </div>

                      <div className="ideas-sidebar__card-actions">
                        <PortalDropdown
                          align="end"
                          menuItems={[
                            {
                              icon: "bi bi-pencil",
                              label: "Edit",
                              onClick: () => handleOpenEdit(idea),
                            },
                            {
                              icon: "bi bi-trash",
                              label: "Delete",
                              danger: true,
                              onClick: () => requestDeleteIdea(idea),
                            },
                          ]}
                        >
                          <button
                            type="button"
                            className="ideas-sidebar__menu-trigger"
                            onMouseDown={(event) => event.stopPropagation()}
                            aria-label="Ideas actions"
                            title="Actions"
                          >
                            <i className="bi bi-three-dots-vertical"></i>
                          </button>
                        </PortalDropdown>
                      </div>
                    </div>
                  </article>
                );
              })}

            {!loading && !error && filteredIdeas.length > MAX_VISIBLE_IDEAS ? (
              <div className="d-flex justify-content-center ideas-sidebar__expand-row">
                <button
                  type="button"
                  className="ideas-sidebar__expand-toggle"
                  onClick={() => setShowIdeasChatModal(true)}
                >
                  Expand
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <IdeasExpandMorePopup
        show={showIdeasModal}
        handleClose={() => setShowIdeasModal(false)}
        ideas={filteredIdeas}
        loading={loading}
        error={error}
        searchTerm={searchTerm}
        selectedTags={selectedTags}
        availableTagOptions={availableTagOptions}
        hasActiveFilters={hasActiveFilters}
        onSearchTermChange={setSearchTerm}
        onSelectedTagsChange={setSelectedTags}
        onClearFilters={() => {
          setSearchTerm("");
          setSelectedTags([]);
        }}
        onCreateIdea={handleOpenCreate}
        onEditIdea={handleOpenEdit}
        onDeleteIdea={requestDeleteIdea}
        onBulkDelete={() => setShowBulkDeleteConfirmation(true)}
        bulkDeleteDisabled={loading || filteredIdeas.length === 0}
        isBulkDeleting={isBulkDeletingIdeas}
      />

      <ConfirmationPopup
        show={Boolean(ideaToDelete)}
        handleClose={() => {
          if (!isDeletingIdea) {
            setIdeaToDelete(null);
          }
        }}
        title="Delete Ideas"
        body={`Are you sure you want to delete "${ideaToDelete?.title || "this entry"}"? This action cannot be undone.`}
        handleConfirm={() => handleDeleteIdea(ideaToDelete)}
        isLoading={isDeletingIdea}
      />

      <ConfirmationPopup
        show={showBulkDeleteConfirmation}
        handleClose={() => {
          if (!isBulkDeletingIdeas) {
            setShowBulkDeleteConfirmation(false);
          }
        }}
        title="Delete All Ideas"
        body={`Are you sure you want to delete ${filteredIdeas.length} idea${filteredIdeas.length === 1 ? "" : "s"}? This action cannot be undone.`}
        handleConfirm={handleBulkDeleteIdeas}
        isLoading={isBulkDeletingIdeas}
      />

      {showAddIdeaModal ? (
        <QuickIdeaPopup
          key={ideaToEdit?.id || "new-idea"}
          show={showAddIdeaModal}
          onClose={handleCloseModal}
          onSaved={() => {
            if (ideaToEdit?.id) {
              handleCloseModal();
            }
          }}
          userId={userId}
          sessionId={normalizedSessionId}
          ideaToEdit={ideaToEdit}
          onDelete={async (idea) => {
            const deleted = await handleDeleteIdea(idea);
            if (deleted) {
              handleCloseModal();
            }
          }}
        />
      ) : null}

      <IdeasExpandAllPopup
        show={showIdeasChatModal}
        handleClose={() => setShowIdeasChatModal(false)}
        userId={userId}
      />
    </>
  );
});

IdeasList.displayName = "IdeasList";

export default IdeasList;
